import { getCameraZone, getZoneId } from 'src/components/Camera/helpers/camera-zone-helpers';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export const CAMERA_COND_NAMES = {
	MOTION: 'camera_motion',
	PRIVACY: 'camera_privacy',
	ARM: 'camera_arm',
	ZONE_MOTION: 'camera_zone_motion',
} as const;

export const CAMERA_SCENE_TOKENS = {
	MOTION: 'cam_motion',
	PRIVACY: 'cam_privacy',
	ARM: 'cam_arm',
	ZONE_MOTION: 'camzone_motion',
} as const;

export const CAMERA_BOOLEAN_VALUES = ['true', 'false'] as const;

export const CAMERA_ID_OFFSET = 1;

export type CameraCondName = (typeof CAMERA_COND_NAMES)[keyof typeof CAMERA_COND_NAMES];

const CAMERA_COND_TO_SCENE_TOKEN: Record<CameraCondName, string> = {
	[CAMERA_COND_NAMES.MOTION]: CAMERA_SCENE_TOKENS.MOTION,
	[CAMERA_COND_NAMES.PRIVACY]: CAMERA_SCENE_TOKENS.PRIVACY,
	[CAMERA_COND_NAMES.ARM]: CAMERA_SCENE_TOKENS.ARM,
	[CAMERA_COND_NAMES.ZONE_MOTION]: CAMERA_SCENE_TOKENS.ZONE_MOTION,
};

/**
 * Build an IoTS scene token for a camera condition.
 * UI channel/zone ids are 0-based; the token suffix is id + {@link CAMERA_ID_OFFSET}.
 */
export const buildCameraSceneToken = (condName: string, id: number | string): string | null => {
	const prefix = CAMERA_COND_TO_SCENE_TOKEN[condName as CameraCondName];
	if (!prefix) {
		return null;
	}
	return `${prefix}${Number(id) + CAMERA_ID_OFFSET}`;
};

export const isCameraUiCondName = (condName: string): boolean => {
	return (Object.values(CAMERA_COND_NAMES) as string[]).includes(condName);
};

export const isCameraZoneSceneCommand = (sceneCommand: string): boolean => {
	return /^camzone_motion\d+$/.test(sceneCommand);
};

export const isCameraSceneCommand = (sceneCommand: string): boolean => {
	return (
		/^cam_motion\d+$/.test(sceneCommand) ||
		/^cam_privacy\d+$/.test(sceneCommand) ||
		/^cam_arm\d+$/.test(sceneCommand) ||
		isCameraZoneSceneCommand(sceneCommand)
	);
};

/**
 * Extract device zone id from a camzone_motion{zoneId+1} scene token.
 */
export const extractZoneIdFromCameraZoneCondition = (sceneCommand: string): string | null => {
	const match = sceneCommand.match(/^camzone_motion(\d+)$/);
	if (!match?.[1]) {
		return null;
	}
	return String(Number(match[1]) - CAMERA_ID_OFFSET);
};

/**
 * Normalize scene/UI boolean values to the wizard token format ('true' | 'false').
 */
export const normalizeCameraConditionValue = (value: unknown): string => {
	if (value === true || value === 'true' || value === 1 || value === '1') {
		return 'true';
	}
	return 'false';
};

/**
 * Convert wizard boolean token to IoTS scene boolean.
 */
export const convertCameraUiValueToSceneValue = (value: string): boolean => {
	return value === 'true';
};

export type CameraSceneCommandParseResult = {
	name: CameraCondName;
	value: string;
	channel: number;
	add_info_ind?: string;
};

/**
 * Parse an IoTS camera scene command back into UI condition fields.
 */
export const parseCameraSceneCommand = (
	sceneCommand: string,
	tshvalue?: unknown,
): CameraSceneCommandParseResult | null => {
	const value = normalizeCameraConditionValue(tshvalue);

	const motionMatch = sceneCommand.match(/^cam_motion(\d+)$/);
	if (motionMatch?.[1]) {
		return {
			name: CAMERA_COND_NAMES.MOTION,
			value,
			channel: Number(motionMatch[1]) - CAMERA_ID_OFFSET,
		};
	}

	const privacyMatch = sceneCommand.match(/^cam_privacy(\d+)$/);
	if (privacyMatch?.[1]) {
		return {
			name: CAMERA_COND_NAMES.PRIVACY,
			value,
			channel: Number(privacyMatch[1]) - CAMERA_ID_OFFSET,
		};
	}

	const armMatch = sceneCommand.match(/^cam_arm(\d+)$/);
	if (armMatch?.[1]) {
		return {
			name: CAMERA_COND_NAMES.ARM,
			value,
			channel: Number(armMatch[1]) - CAMERA_ID_OFFSET,
		};
	}

	const zoneMatch = sceneCommand.match(/^camzone_motion(\d+)$/);
	if (zoneMatch?.[1]) {
		return {
			name: CAMERA_COND_NAMES.ZONE_MOTION,
			value,
			channel: 0,
			add_info_ind: String(Number(zoneMatch[1]) - CAMERA_ID_OFFSET),
		};
	}

	return null;
};

/**
 * Resolve a camera zone display name from device settings.
 */
export const getCameraZoneName = (
	zoneId: string | number,
	deviceSettings?: DeviceSettingsG2 | null,
): string => {
	const zone = getCameraZone(deviceSettings, Number(zoneId));
	return zone?.name || `Zone ${zoneId}`;
};

/**
 * Whether a camerazone component key refers to a motion (not privacy) zone.
 */
export const isMotionCameraZoneComponent = (
	componentKey: string,
	deviceSettings?: DeviceSettingsG2 | null,
): boolean => {
	const zoneId = getZoneId(componentKey);
	const zone = getCameraZone(deviceSettings, zoneId);
	if (!zone) {
		return false;
	}
	return zone.type !== 'privacy';
};
